import React, { useContext, useEffect, useState } from "react";
import PagePreview from "./Preview";
import { useSelector } from "react-redux";
import { getData } from "~/functions/gridFunctions";
import PageContainer from "~/components/PageContainer";
import { FullGridContext } from "../..";
import { format, parseISO } from "date-fns";
import { AlertContext } from "~/App";
import api from "~/services/api";
import { toast } from "react-toastify";

export default function PayeesSettlement() {
  const filial = useSelector((state) => state.auth.filial);
  const defaultOrderBy = { column: "created_at", asc: false };
  const defaultGridHeader = [
    {
      title: "Issuer Name",
      name: ["issuer", "name"],
      type: "text",
      filter: false,
    },
    {
      title: "Filial Name",
      name: ["filial", "name"],
      type: "text",
      filter: true,
    },
    {
      title: "Invoice Number",
      name: "invoice_number",
      type: "text",
      filter: false,
    },
    {
      title: "Entry Date",
      name: "entry_date",
      type: "date",
      filter: false,
    },
    {
      title: "Due Date",
      name: "due_date",
      type: "date",
      filter: false,
    },
    {
      title: "Amount",
      name: "amount",
      type: "currency",
      filter: false,
    },
    {
      title: "Platform",
      name: ["paymentMethod", "platform"],
      type: "text",
      filter: false,
    },
    {
      title: "Settlement Date",
      name: "created_at",
      type: "date",
      filter: false,
    },
  ];
  const [selected, setSelected] = useState([]);
  const { alertBox } = useContext(AlertContext);

  const {
    accessModule,
    activeFilters,
    gridData,
    gridDetails,
    gridHeader,
    handleFilters,
    handleOpened,
    limit,
    loadingData,
    opened,
    orderBy,
    page,
    pages,
    search,
    setActiveFilters,
    setGridData,
    setGridDetails,
    setGridHeader,
    setLimit,
    setLoadingData,
    setOpened,
    setOrderBy,
    setPage,
    setPages,
    setSearch,
    setTotalRows,
    setSuccessfullyUpdated,
    successfullyUpdated,
    totalRows,
  } = useContext(FullGridContext);

  const pageAccess = accessModule.children.find(
    (el) => el.alias === "payees-settlement"
  );

  useEffect(() => {
    setActiveFilters([]);
  }, []);

  async function loader() {
    setLoadingData(true);
    setTimeout(async () => {
      const data = await getData("payeesettlements", {
        limit,
        page,
        orderBy,
        setPages,
        setGridData,
        search,
        defaultGridHeader,
        defaultOrderBy,
        setGridDetails,
      });
      if (!data) {
        return;
      }
      const gridDataValues = data.map(
        (
          {
            id,
            canceled_at,
            payee,
            status,
            amount,
            settlement_date,
            paymentMethod,
            created_at,
            // status_date,
          },
          index
        ) => {
          const ret = {
            show: true,
            id,
            fields: [
              payee.issuer?.name ?? "",
              payee.filial?.name ?? "",
              payee.invoice_number?.toString() ?? "",
              format(parseISO(payee.entry_date), "yyyy-MM-dd"),
              format(parseISO(payee.due_date), "yyyy-MM-dd"),
              "$ " + amount.toFixed(2),
              paymentMethod ? paymentMethod.platform : "",
              settlement_date
                ? format(parseISO(settlement_date), "yyyy-MM-dd")
                : format(parseISO(created_at), "yyyy-MM-dd"),
              ,
            ],
            selectable: paymentMethod?.platform !== "Gravity - Online",

            canceled: canceled_at,
            page: Math.ceil((index + 1) / limit),
          };
          return ret;
        }
      );
      setGridData(gridDataValues);
      setLoadingData(false);
    }, 500);
  }

  useEffect(() => {
    loader();
  }, [opened, filial, orderBy, search, limit, page]);

  useEffect(() => {
    let issuer = null;
    let differentIssuer = null;
    if (selected.length > 0) {
      selected.map(async (payee) => {
        if (issuer) {
          if (payee.fields[0] !== issuer) {
            differentIssuer = payee.fields[0];
          }
        }
        issuer = payee.fields[0];
      });
    }
    if (differentIssuer) {
      alertBox({
        title: "Attention!",
        descriptionHTML: `<p>You have selected payees from different issuers. Please select only payees from the same issuer.</p>`,
        buttons: [
          {
            title: "Ok",
            class: "cancel",
          },
        ],
      });
      setSelected(
        selected.filter((payee) => payee.fields[0] !== differentIssuer)
      );
    }
  }, [selected]);

  function handleDelete() {
    if (selected.length === 0) {
      return;
    }
    alertBox({
      title: "Attention!",
      descriptionHTML:
        "Are you sure you want to delete the selected settlements? This action cannot be undone.",
      buttons: [
        {
          title: "No",
          class: "cancel",
        },
        {
          title: "Yes",
          onPress: async () => {
            try {
              const promises = [];
              selected.map((payee) => {
                promises.push(
                  api
                    .delete(`/payeesettlements/${payee.id}`)
                    .then((response) => {
                      toast(response.data.message, { autoClose: 1000 });
                    })
                    .catch((err) => {
                      toast(err.response.data.error, {
                        type: "error",
                        autoClose: 5000,
                      });
                    })
                );
              });
              Promise.all(promises).then(() => {
                loader();
                setSelected([]);
              });
            } catch (err) {
              toast(err.response.data.error, {
                type: "error",
                autoClose: 3000,
              });
            }
          },
        },
      ],
    });
  }

  return (
    <PageContainer
      FullGridContext={FullGridContext}
      PagePreview={null}
      pageAccess={pageAccess}
      defaultGridHeader={defaultGridHeader}
      handleNew={false}
      handleEdit={false}
      selection={{
        multiple: true,
        selected,
        setSelected,
        functions: [
          {
            title: "Delete settlement",
            fun: handleDelete,
            icon: "X",
            Page: null,
            opened: null,
            setOpened: () => null,
            selected,
          },
        ],
      }}
    />
  );
}
